import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Truck, Phone, Clock, Pizza, Utensils, CakeSlice } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PhotoHero, Reveal, Img, Container, SectionTitle } from "@/components/common/Primitives";
import { MenuCard } from "@/components/dining/MenuCard";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { useLang } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";
import { SIZES } from "@/lib/images";
import { HOTEL } from "@/lib/hotel";

const OFFER_IMAGES = {
  komle: { src: "/images/flyer-komle.jpg", position: "50% 50%" },
  pizza: { src: "/images/garden-tables.jpg", position: "50% 60%" },
  sunday: { src: "/images/restaurant-window.jpg", position: "50% 40%" },
  birthday: { src: "/images/cafe-birthday.jpg", position: "50% 35%" },
};

const HoursCard = ({ title, icon: I, rows, tr, testid }) => (
  <div className="rounded-[24px] bg-card px-6 py-2 ring-hairline sm:px-8" data-testid={testid}>
    <div className="flex items-center gap-3 border-b border-border py-5">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-foreground"><I className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden /></span>
      <h3 className="text-[1.125rem] font-semibold tracking-[-0.01em] text-foreground">{title}</h3>
    </div>
    <ul>
      {rows.map((h) => (
        <li key={h.key} className="flex items-baseline justify-between gap-6 border-b border-border py-4 last:border-0" data-testid={`dining-hours-${h.key}`}>
          <span className="text-[0.9375rem] text-foreground">{tr(h.label)}</span>
          <span className="shrink-0 text-[0.9375rem] font-semibold tabular text-foreground">{h.value}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function Dining() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  const { ask } = useConcierge();
  const d = data?.dining;
  usePageMeta(
    tx("Mat", "Dining"),
    tx("Restaurant med fjordutsikt, Strandkaféen med steinomnspizza på kaien, komletorsdag og søndagsmiddag. Sjå menyar og opningstider.", "Restaurant with fjord view, Strandkaféen with stone-oven pizza on the quay, komle Thursdays and Sunday dinner. See menus and opening hours.")
  );
  const offers = d?.offers || [];
  const menus = d?.menus || [];
  const pizza = menus.find((m) => m.id === "pizza");
  const party = menus.find((m) => m.id === "selskap");
  const hours = d?.hours || [];
  const restaurantHours = hours.filter((h) => !h.key.startsWith("cafe"));
  const cafeHours = hours.filter((h) => h.key.startsWith("cafe"));
  const askTable = () => ask(lang === "en" ? "I would like to book a table for dinner. Which day and time are possible?" : "Eg vil gjerne bestille bord til middag. Kva dag og tid er mogleg?");

  return (
    <div data-testid="dining-page">
      <PhotoHero
        testid="dining-hero"
        image="/images/restaurant-hall.jpg"
        mobileImage="/images/restaurant-window.jpg"
        position="50% 62%"
        mobilePosition="50% 42%"
        alt={tx("Restauranten på Skånevik Fjordhotel, dekka til middag", "The restaurant at Skånevik Fjordhotel, set for dinner")}
        kicker={tx("Restauranten · Strandkaféen · Komletorsdag", "The restaurant · Strandkaféen · Komle Thursday")}
        title={tx("Mat ved kaien", "Food on the quay")}
        lead={tx("Frukost, middag og søndagsmiddag i restauranten med utsikt over fjorden. Nede på kaien ligg Strandkaféen med steinomn, kaffi og is.", "Breakfast, dinner and Sunday dinner in the restaurant overlooking the fjord. Down on the quay, Strandkaféen has a stone oven, coffee and ice cream.")}
        caption={tx("Restauranten tek 90 til bords. Vindauga vender mot hagen og fjorden.", "The restaurant seats 90. The windows face the garden and the fjord.")}
      >
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Button size="lg" variant="inverse" onClick={askTable} data-testid="dining-book-table-cta"><MessageCircle className="h-4 w-4" /> {tx("Bestill bord", "Book a table")}</Button>
          <Button asChild size="lg" variant="ghost-light" className="border border-white/25" data-testid="dining-hero-menu-link"><a href="#meny">{tx("Sjå menyane", "See the menus")} <ArrowRight className="h-4 w-4" /></a></Button>
        </div>
      </PhotoHero>

      {/* Two places to eat */}
      <section className="pt-20 lg:pt-32" data-testid="dining-venues">
        <Container>
          <SectionTitle size="lg" title={tx("To stader å ete", "Two places to eat")} sub={tx("Restauranten i hovudbygget heile året. Strandkaféen på kaien kvar dag i sommarsesongen og i helgane elles.", "The restaurant in the main building all year. Strandkaféen on the quay every day in summer and at weekends otherwise.")} testid="dining-venues-title" />
          <div className="mt-10 grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-10">
            <Reveal>
              <article data-testid="dining-venue-restaurant">
                <div className="grid grid-cols-5 gap-3">
                  <Img src="/images/restaurant-window.jpg" alt={tx("Dekka bord ved vindauget med utsikt over fjorden", "A set table by the window overlooking the fjord")} className="img-zoom col-span-3 aspect-[4/5]" radius="rounded-[22px]" sizes={SIZES.third} position="50% 40%" />
                  <Img src="/images/food-asparagus.jpg" alt={tx("Asparges frå kjøkenet", "Asparagus from the kitchen")} className="img-zoom col-span-2 aspect-[4/5]" radius="rounded-[22px]" sizes={SIZES.quarter} />
                </div>
                <div className="mt-6 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{tx("I hovudbygget · heile året", "In the main building · all year")}</p>
                    <h3 className="mt-1 text-[1.5rem] leading-tight tracking-[-0.02em] text-foreground">{tx("Restauranten", "The restaurant")}</h3>
                  </div>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground"><Utensils className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden /></span>
                </div>
                <p className="mt-3 max-w-[56ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{tx("Frukostbuffé kvar morgon, middag måndag til laurdag og søndagsmiddag frå kl. 13. Kjøkenet lagar maten frå botnen av, med råvarer frå Etne og Kvinnherad. Her held me også selskap, julebord og konferansemiddagar.", "Breakfast buffet every morning, dinner Monday to Saturday and Sunday dinner from 13:00. The kitchen cooks from scratch with produce from Etne and Kvinnherad. This is also where we host celebrations, Christmas dinners and conference dinners.")}</p>
                <ul className="mt-4 flex flex-wrap gap-2 text-xs font-medium text-foreground">
                  {[tx("Frukost 07.30–10.00", "Breakfast 07:30–10:00"), tx("Middag 17.00–21.00", "Dinner 17:00–21:00"), tx("90 til bords", "Seats 90")].map((t) => <li key={t} className="rounded-full bg-secondary px-3 py-1.5 tabular">{t}</li>)}
                </ul>
              </article>
            </Reveal>
            <Reveal delay={0.06}>
              <article data-testid="dining-venue-cafe">
                <div className="grid grid-cols-5 gap-3">
                  <Img src="/images/cafe-chalk-counter.jpg" alt={tx("Disken på Strandkaféen med teikneveggen for born", "The counter at Strandkaféen with the chalkboard wall for kids")} className="img-zoom col-span-3 aspect-[4/5]" radius="rounded-[22px]" sizes={SIZES.third} position="40% 50%" />
                  <div className="col-span-2 flex flex-col gap-3">
                    <Img src="/images/strandkafeen-sign.jpg" alt={tx("Skiltet til Strandkaféen", "The Strandkaféen sign")} className="img-zoom aspect-[4/3]" radius="rounded-[22px]" sizes={SIZES.quarter} />
                    <Img src="/images/cafe-coffee.jpg" alt={tx("Kaffikoppar på Strandkaféen", "Coffee cups at Strandkaféen")} className="img-zoom flex-1 min-h-[120px]" radius="rounded-[22px]" sizes={SIZES.quarter} />
                  </div>
                </div>
                <div className="mt-6 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{tx("På kaien · juni–august kvar dag", "On the quay · every day June–August")}</p>
                    <h3 className="mt-1 text-[1.5rem] leading-tight tracking-[-0.02em] text-foreground">Strandkaféen</h3>
                  </div>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground"><Pizza className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden /></span>
                </div>
                <p className="mt-3 max-w-[56ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{tx("Steinomnspizza, kaffi, is og noko kaldt i glaset – på fjordterrassen eller i hagen ved bassenget. Born teiknar på tavleveggen medan pizzaen står i omnen. Ta gjerne med pizzaen ned til Peparen.", "Stone-oven pizza, coffee, ice cream and something cold in the glass – on the fjord terrace or in the garden by the pool. Kids draw on the chalkboard wall while the pizza is in the oven. Feel free to take your pizza down to Peparen.")}</p>
                <ul className="mt-4 flex flex-wrap gap-2 text-xs font-medium text-foreground">
                  {[tx("Sesong 11.00–21.00", "In season 11:00–21:00"), tx("7 pizzaer · 299 kr", "7 pizzas · NOK 299"), tx("Fjordterrasse", "Fjord terrace")].map((t) => <li key={t} className="rounded-full bg-secondary px-3 py-1.5 tabular">{t}</li>)}
                </ul>
              </article>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Menus */}
      <section id="meny" className="scroll-mt-24 pt-20 lg:pt-32" data-testid="dining-menus">
        <Container>
          <SectionTitle size="lg" title={tx("Menyane", "The menus")} sub={tx("Pizzamenyen gjeld på Strandkaféen. Selskapsmenyen serverer me i restauranten etter avtale, frå 10 personar.", "The pizza menu is served at Strandkaféen. The party menu is served in the restaurant by arrangement, from 10 guests.")} testid="dining-menus-title" />
          <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start">
            <Reveal>{pizza ? <MenuCard menu={pizza} testid="dining-pizza-menu" /> : <div className="aspect-[3/4] animate-pulse rounded-[28px] bg-muted" />}</Reveal>
            <Reveal delay={0.06}>
              {party ? (
                <MenuCard
                  menu={party}
                  testid="dining-party-menu"
                  cta={<Button asChild variant="soft" size="sm" data-testid="dining-party-cta"><Link to="/arrangement?type=celebration#forespurnad">{tx("Spør om selskapsmeny", "Ask about the party menu")} <ArrowRight className="h-4 w-4" /></Link></Button>}
                />
              ) : <div className="aspect-[3/4] animate-pulse rounded-[28px] bg-muted" />}
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Regular days */}
      <section className="pt-20 lg:pt-32" data-testid="dining-offers">
        <Container>
          <SectionTitle size="lg" title={tx("Faste dagar", "Regular days")} sub={tx("Dagar folk i bygda set av – og gjester planlegg opphaldet rundt.", "Days the village sets aside – and guests plan their stay around.")} testid="dining-offers-title" />
          <div className="mt-10 grid grid-cols-1 gap-x-7 gap-y-12 sm:grid-cols-2">
            {offers.map((o, i) => {
              const img = OFFER_IMAGES[o.id] || { src: o.image };
              const isBirthday = o.id === "birthday";
              return (
                <Reveal key={o.id} delay={(i % 2) * 0.05}>
                  <article className="group" data-testid={isBirthday ? "dining-kids-birthday" : `dining-offer-${o.id}`}>
                    <Img src={img.src} alt={tr(o.title)} className="img-zoom aspect-[16/10]" radius="rounded-[22px]" sizes={SIZES.half} position={img.position} />
                    <div className="mt-5 flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground tabular">{tr(o.when)}</p>
                        <h3 className="mt-1.5 text-[1.375rem] leading-tight tracking-[-0.02em] text-foreground sm:text-2xl">{tr(o.title)}</h3>
                      </div>
                      {isBirthday && <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground"><CakeSlice className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden /></span>}
                    </div>
                    <p className="mt-3 max-w-[56ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{tr(o.text)}</p>
                    {isBirthday && (
                      <Button variant="soft" className="mt-5" onClick={() => ask(lang === "en" ? "I would like to book a children's birthday party at Strandkaféen." : "Eg vil gjerne bestille barnebursdag på Strandkaféen.")} data-testid="dining-kids-birthday-cta">{tx("Spør om barnebursdag", "Ask about a kids' birthday")} <ArrowRight className="h-4 w-4" /></Button>
                    )}
                    {o.id === "komle" && (
                      <Button asChild variant="soft" className="mt-5" data-testid="dining-komle-call"><a href={HOTEL.tel}><Phone className="h-4 w-4" /> {tx("Ring for bord", "Call for a table")} · <span className="tabular">{HOTEL.phoneDisplay}</span></a></Button>
                    )}
                  </article>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Hours */}
      <section id="opningstider" className="mt-20 scroll-mt-24 bg-secondary/70 py-20 lg:mt-32 lg:py-32" data-testid="dining-opening-hours">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-4">
              <SectionTitle size="lg" title={tx("Opningstider", "Opening hours")} />
              <p className="mt-4 max-w-[44ch] text-base leading-relaxed text-muted-foreground">{tr(d?.hours_note)}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button size="lg" onClick={askTable} data-testid="dining-ask-table"><MessageCircle className="h-4 w-4" /> {tx("Bestill bord", "Book a table")}</Button>
                <Button asChild variant="soft" size="lg" data-testid="dining-hours-call"><a href={HOTEL.tel}><Phone className="h-4 w-4" /> <span className="tabular">{HOTEL.phoneDisplay}</span></a></Button>
              </div>
            </Reveal>
            <Reveal delay={0.06} className="grid grid-cols-1 gap-6 lg:col-span-8 lg:grid-cols-2 lg:items-start">
              <HoursCard title={tx("Restauranten", "The restaurant")} icon={Utensils} rows={restaurantHours} tr={tr} testid="dining-hours-restaurant" />
              <HoursCard title="Strandkaféen" icon={Clock} rows={cafeHours} tr={tr} testid="dining-hours-cafe" />
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Catering */}
      <section className="py-20 lg:py-32" data-testid="dining-catering">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center lg:gap-16">
          <Reveal className="lg:col-span-6">
            <Img src="/images/food-asparagus.jpg" alt={tx("Asparges bunta med hyssing", "Asparagus tied with string")} className="img-zoom aspect-[4/3] lg:aspect-[5/4]" radius="rounded-[24px] sm:rounded-[28px]" sizes={SIZES.half} />
          </Reveal>
          <Reveal delay={0.06} className="lg:col-span-6">
            <h2 className="text-[2rem] leading-[1.02] tracking-[-0.03em] text-foreground sm:text-[2.5rem] lg:text-[2.75rem]">{tx("Mat til selskapet heime", "Food for your party at home")}</h2>
            <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tr(d?.catering_intro)}</p>
            <ul className="mt-8 divide-y divide-border border-y border-border" data-testid="dining-catering-list">
              {(d?.catering || []).map((c) => (
                <li key={c.id} className="py-3.5 text-[1.0625rem] font-medium text-foreground" data-testid={`dining-catering-${c.id}`}>{tr(c.title)}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Button asChild size="lg" data-testid="dining-catering-cta"><Link to="/arrangement?type=celebration#forespurnad">{tx("Spør om catering", "Ask about catering")} <ArrowRight className="h-4 w-4" /></Link></Button>
              <span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Truck className="h-4 w-4" /> {tx("Levering i Etne og Kvinnherad etter avtale", "Delivery in Etne and Kvinnherad by arrangement")}</span>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Book a table */}
      <section id="bord" className="scroll-mt-24 pb-20 lg:pb-32" data-testid="dining-book-table">
        <Container>
          <div className="grain overflow-hidden rounded-[28px] bg-primary px-6 py-10 text-primary-foreground sm:px-10 sm:py-12 lg:flex lg:items-center lg:justify-between lg:gap-12 lg:rounded-[32px] lg:px-14">
            <div className="max-w-xl">
              <p className="text-sm font-semibold text-white/60">{tx("Bordbestilling", "Table reservations")}</p>
              <h2 className="mt-3 text-[1.875rem] leading-[1.02] tracking-[-0.03em] text-white sm:text-[2.25rem]">{tx("Bord til middag?", "A table for dinner?")}</h2>
              <p className="mt-3 text-[1.0625rem] leading-relaxed text-white/70">{tx("Send ein førespurnad til Fjordverten eller ring oss. Me stadfestar bordet på e-post eller telefon – som regel innan ein time på dagtid.", "Send a request to Fjordverten or call us. We confirm the table by email or phone – usually within an hour during the day.")}</p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3 lg:mt-0 lg:shrink-0">
              <Button size="lg" variant="inverse" onClick={askTable} data-testid="dining-book-table-bottom"><MessageCircle className="h-4 w-4" /> {tx("Bestill bord", "Book a table")}</Button>
              <Button asChild size="lg" variant="ghost-light" className="border border-white/20" data-testid="dining-book-table-call"><a href={HOTEL.tel}><Phone className="h-4 w-4" /> <span className="tabular">{HOTEL.phoneDisplay}</span></a></Button>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
